import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  type BriefConceptImageContext,
  type PromptModeOption,
  type PromptTarget,
  buildImageRequest,
} from '@/components/concept-workbench/prompt-target';
import { supabase } from '@/integrations/supabase/client';
import {
  isFlatDesignDirection,
  recomposeFlatDesignInstruction,
} from '@/lib/concept-workbench/derive-design-prompt';
import type { AspectRatioToken } from '@/lib/concept-workbench/types';
import { useVariantSetStore } from '@/lib/concept-workbench/useVariantSetStore';
import { rewriteCaption } from '@/services/media/caption-rewrite';
import { logger } from '@/utils/logger';

/**
 * The prompt bar's three dispatch paths, kept apart on purpose.
 *
 * The target decides the endpoint BEFORE the request is built, so a prompt aimed
 * at a headline can never spend an image credit and a prompt aimed at the image
 * can never silently rewrite copy. Nothing here classifies free text.
 *
 * Errors surface the SERVER's own message. `/api/generate-image` returns a
 * specific reason — moderation block, daily cap, auth — and "Something went
 * wrong" would throw away the only part the user can act on.
 */

interface GenerateImageResponse {
  imageUrls?: string[];
  mediaIds?: (string | null)[];
  error?: string;
  retryable?: boolean;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * A different size's current image to generate from, instead of the selected size's own (empty)
 * one. It is what makes "Generate this size" carry an edited design forward.
 */
export interface SubmitReference {
  imageUrl: string;
  mode: PromptModeOption;
  recompose?: boolean;
}

export interface SubmitOptions {
  /**
   * Generate from the Concept alone, ignoring the selected size's own image. This is "Regenerate":
   * a new image is made by the current renderer and attached to the size, instead of an edit of the
   * old one. The replaced image stays in the library (it is a `media` row, never deleted here).
   */
  fresh?: boolean;
  /**
   * Generate for THIS size instead of the selected one. It lets "Regenerate all sizes" walk every
   * size through the same path as a single Regenerate without changing the selection.
   */
  variant?: { id: string; aspectRatio: AspectRatioToken };
}

export interface WorkbenchPromptState {
  value: string;
  setValue: (value: string) => void;
  pending: boolean;
  status: string | null;
  error: string | null;
  /**
   * `promptOverride` exists for the canvas empty state's `Generate this size`,
   * which composes its prompt from the brief rather than the input. Setting the
   * input and submitting in the same tick would read the PREVIOUS state value.
   */
  submit: (
    mode?: PromptModeOption,
    promptOverride?: string,
    reference?: SubmitReference,
    options?: SubmitOptions,
  ) => Promise<void>;
  /** The last `submit` for an image: true only when the new image was attached to its size. */
  lastImageAttached: () => boolean;
  retryAttachment: () => Promise<void>;
  recovery: { renditionId: string; mediaId: string; aspectRatio: AspectRatioToken } | null;
}

export function useWorkbenchPrompt(args: {
  target: PromptTarget;
  variantId: string | null;
  aspectRatio: AspectRatioToken | null;
  currentImageUrl: string | null;
  briefContext: BriefConceptImageContext | null;
  renditionLifecycle: {
    setGenerating: (
      renditionId: string,
      isGenerating: boolean,
      generationToken?: string,
    ) => Promise<void>;
    attachMedia: (renditionId: string, mediaId: string) => Promise<void>;
  };
}): WorkbenchPromptState {
  const { target, variantId, aspectRatio, currentImageUrl, briefContext, renditionLifecycle } =
    args;
  const [value, setValue] = useState('');
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<{
    renditionId: string;
    mediaId: string;
    aspectRatio: AspectRatioToken;
  } | null>(null);

  const setGenerating = useVariantSetStore((state) => state.setGenerating);
  const updateSetLayer = useVariantSetStore((state) => state.updateSetLayer);

  const attachedRef = useRef(false);
  const lastImageAttached = useCallback(() => attachedRef.current, []);

  const submit = useCallback(
    async (
      mode?: PromptModeOption,
      promptOverride?: string,
      reference?: SubmitReference,
      options?: SubmitOptions,
    ) => {
      const prompt = (promptOverride ?? value).trim();
      if (prompt.length === 0 || pending) return;
      setError(null);

      if (!options?.variant && target.kind === 'copy' && target.layerId) {
        setPending(true);
        setStatus('Rewriting this text…');
        try {
          const { data } = await supabase.auth.getSession();
          const original =
            useVariantSetStore
              .getState()
              .document?.layers.find((layer) => layer.id === target.layerId) ?? null;
          if (!original || original.kind !== 'text') throw new Error('That layer has no text.');
          const rewritten = await rewriteCaption({
            original: original.text,
            instruction: prompt,
            accessToken: data.session?.access_token ?? '',
          });
          updateSetLayer(target.layerId, { text: rewritten });
          setValue('');
        } catch (cause) {
          logger.error('Workbench copy rewrite failed', cause);
          setError(cause instanceof Error ? cause.message : 'Rewrite failed.');
        } finally {
          setPending(false);
          setStatus(null);
        }
        return;
      }

      if (!options?.variant && target.kind === 'layer') {
        // No endpoint returns a typed shape/logo patch today. Saying so is the
        // correct behaviour; falling through to an image edit would spend a
        // credit on an operation the user did not ask for.
        setError('Restyling a shape or logo from a prompt is not wired up yet.');
        return;
      }

      const targetVariantId = options?.variant?.id ?? variantId;
      const targetAspectRatio = options?.variant?.aspectRatio ?? aspectRatio;
      if (!targetVariantId || !targetAspectRatio) return;
      attachedRef.current = false;
      setPending(true);
      setStatus(
        mode?.editMode === 'outpaint'
          ? `Extending the image to ${targetAspectRatio}…`
          : `Generating ${targetAspectRatio}…`,
      );
      /**
       * THE DURABLE-CORRELATION TOKEN (LANE IMG-DONE). Minted BEFORE the
       * request goes out and stamped onto the rendition by `setGenerating`
       * (`generation_token` + `generation_started_at`). The server's own
       * completion write (`attachGeneratedMediaByCorrelation`, called from
       * inside `/api/generate-image` itself once the media row exists) finds
       * this exact rendition by (concept, aspect ratio, token) and attaches
       * the media THERE — not only via this function's own `attachMedia`
       * call below, which still runs for the common case but is no longer
       * the ONLY path. A reload, HMR, or crash between the fetch going out
       * and this function resuming no longer stalls the row at
       * `is_generating: true` forever: either the server already attached it,
       * or `reconcileStaleGenerations` (mount/poll, `useConceptRenditions`)
       * eventually turns it into an honest failed state with a retry button.
       */
      const generationToken = crypto.randomUUID();
      try {
        await renditionLifecycle.setGenerating(targetVariantId, true, generationToken);
      } catch (cause) {
        setError(
          cause instanceof Error
            ? cause.message
            : 'Could not reserve this rendition for generation.',
        );
        setPending(false);
        setStatus(null);
        return;
      }
      try {
        const effectiveMode = reference?.mode ?? mode;
        const recompose =
          Boolean(reference?.recompose) ||
          (effectiveMode?.editMode === 'outpaint' &&
            isFlatDesignDirection(briefContext?.visualDirection));
        const body = buildImageRequest({
          prompt:
            recompose && !reference?.recompose
              ? recomposeFlatDesignInstruction(targetAspectRatio)
              : prompt,
          aspectRatio: targetAspectRatio,
          currentImageUrl: options?.fresh ? null : (reference?.imageUrl ?? currentImageUrl),
          mode: effectiveMode,
          ...(recompose ? { recompose: true } : {}),
          ...(reference ? { supersedeVisualDirection: true } : {}),
          ...(briefContext ? { briefContext, renditionId: targetVariantId, generationToken } : {}),
        });
        const response = await fetch('/api/generate-image', {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify(body),
        });
        const payload = (await response.json().catch(() => null)) as GenerateImageResponse | null;
        if (!response.ok || payload?.error) {
          throw new Error(payload?.error ?? `Generation failed (${response.status}).`);
        }
        const mediaId = payload?.mediaIds?.find((id): id is string => Boolean(id)) ?? null;
        if (!mediaId) {
          throw new Error('The image generated but was not saved to your library.');
        }
        try {
          await renditionLifecycle.attachMedia(targetVariantId, mediaId);
          attachedRef.current = true;
          setRecovery(null);
          setValue('');
          toast.success(`${targetAspectRatio} attached`);
        } catch (cause) {
          logger.error('Workbench media attachment failed after save', cause);
          setRecovery({ renditionId: targetVariantId, mediaId, aspectRatio: targetAspectRatio });
          setError('Saved to Assets but not attached to this size. Retry the attachment.');
        }
      } catch (cause) {
        logger.error('Workbench image generation failed', cause);
        setError(cause instanceof Error ? cause.message : 'Generation failed.');
      } finally {
        try {
          await renditionLifecycle.setGenerating(targetVariantId, false);
        } catch (cause) {
          logger.error('Workbench generation completion flag failed', cause);
        }
        setGenerating(targetVariantId, false);
        setPending(false);
        setStatus(null);
      }
    },
    [
      value,
      pending,
      target,
      variantId,
      aspectRatio,
      currentImageUrl,
      setGenerating,
      updateSetLayer,
      briefContext,
      renditionLifecycle,
    ],
  );

  const retryAttachment = useCallback(async () => {
    if (!recovery) return;
    setError(null);
    try {
      await renditionLifecycle.attachMedia(recovery.renditionId, recovery.mediaId);
      setRecovery(null);
      toast.success(`${recovery.aspectRatio} attached`);
    } catch (cause) {
      logger.error('Workbench media attachment retry failed', cause);
      setError('Saved to Assets but not attached to this size. Retry the attachment.');
    }
  }, [recovery, renditionLifecycle]);

  return {
    value,
    setValue,
    pending,
    status,
    error,
    submit,
    lastImageAttached,
    retryAttachment,
    recovery,
  };
}
